import * as THREE from "three";
import { feature } from "topojson-client";
import type { FeatureCollection, Polygon, MultiPolygon } from "geojson";
import { latLngToPlane } from "./projection";
import { colorNum } from "../theme/tokens";

/**
 * Flat-map landmass layer.
 *
 * Each continent polygon is projected to the XY plane via Natural Earth,
 * filled with `geoLand`, and outlined with a thin `geoStroke`. Polygons that
 * cross the antimeridian are split at the discontinuity so there's no
 * line crossing the full width of the map.
 */
export async function buildGeography(): Promise<THREE.Group> {
  const group = new THREE.Group();
  group.name = "geography";

  const topoRes = await fetch(
    "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/land-110m.json",
  );
  const topo = await topoRes.json();
  const land = feature(topo, topo.objects.land) as unknown as FeatureCollection<
    Polygon | MultiPolygon
  >;

  const landMat = new THREE.MeshBasicMaterial({
    color: colorNum.geoLand,
    transparent: true,
    opacity: 0.82,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const strokeMat = new THREE.LineBasicMaterial({
    color: colorNum.geoStroke,
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
  });

  for (const f of land.features) {
    const polys: number[][][][] =
      f.geometry.type === "Polygon"
        ? [f.geometry.coordinates]
        : f.geometry.coordinates;

    for (const poly of polys) {
      const outer = poly[0];
      if (!outer || outer.length < 3) continue;

      // FILL — project all vertices, then surgically remove any that create
      // oversized edges (antimeridian hops, Arctic long-jumps). The rest of
      // the polygon fills normally. This preserves continental fill while
      // dropping only the 2-3 vertices that cause artifacts (e.g., Alaska's
      // Aleutian tail wrapping to the other side of the map).
      const projected = outer.map(([lng, lat]) => latLngToPlane(lat, lng));
      const cleaned: [number, number][] = [];
      const thresh2 = MAX_SEGMENT_LEN * MAX_SEGMENT_LEN;
      for (let i = 0; i < projected.length; i++) {
        if (cleaned.length > 0) {
          const prev = cleaned[cleaned.length - 1];
          const dx = projected[i][0] - prev[0];
          const dy = projected[i][1] - prev[1];
          if (dx * dx + dy * dy > thresh2) continue; // skip jumped vertex
        }
        cleaned.push(projected[i]);
      }
      // Check the closing edge (last → first). If too long, trim from the
      // end until it's reasonable. Handles polygons that start near the wrap.
      while (cleaned.length >= 4) {
        const f = cleaned[0];
        const l = cleaned[cleaned.length - 1];
        const dx = f[0] - l[0];
        const dy = f[1] - l[1];
        if (dx * dx + dy * dy <= thresh2) break;
        cleaned.pop();
      }
      if (cleaned.length >= 3) {
        const shape = new THREE.Shape();
        shape.moveTo(cleaned[0][0], cleaned[0][1]);
        for (let i = 1; i < cleaned.length; i++) {
          shape.lineTo(cleaned[i][0], cleaned[i][1]);
        }
        const geo = new THREE.ShapeGeometry(shape);
        const mesh = new THREE.Mesh(geo, landMat);
        mesh.position.z = 0;
        group.add(mesh);
      }

      // STROKES — break any segment longer than MAX_SEGMENT_LEN. This
      // catches antimeridian wraps, Arctic long-hops, and any other
      // pathological connection that produces horizontal streaks.
      for (const ring of poly) {
        const projected = projectAndSplitStroke(ring);
        const shouldClose = projected.length === 1;
        for (const segment of projected) {
          if (segment.length < 2) continue;
          const positions: number[] = [];
          for (const [x, y] of segment) positions.push(x, y, 0.001);
          if (shouldClose) {
            positions.push(segment[0][0], segment[0][1], 0.001);
          }
          const g = new THREE.BufferGeometry();
          g.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(positions, 3),
          );
          group.add(new THREE.Line(g, strokeMat));
        }
      }
    }
  }

  return group;
}

/**
 * Max projected distance between consecutive points. Any segment longer
 * than this is a rendering artifact (antimeridian wrap, Arctic long-hop,
 * or polar compression anomaly). Breaking the line here is generic —
 * no latitude guessing needed.
 */
const MAX_SEGMENT_LEN = 1.2;

/**
 * Project and split for strokes — breaks any segment longer than
 * MAX_SEGMENT_LEN. Catches antimeridian wraps, Arctic artifacts, and
 * any other pathological long-hop connection generically.
 */
function projectAndSplitStroke(ring: number[][]): [number, number][][] {
  const segments: [number, number][][] = [];
  let current: [number, number][] = [];
  let prev: [number, number] | null = null;

  for (const [lng, lat] of ring) {
    const p = latLngToPlane(lat, lng);
    if (prev) {
      const dx = p[0] - prev[0];
      const dy = p[1] - prev[1];
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > MAX_SEGMENT_LEN) {
        if (current.length >= 2) segments.push(current);
        current = [];
      }
    }
    current.push(p);
    prev = p;
  }
  if (current.length >= 2) segments.push(current);
  return segments;
}

